'use client';

import React, { useState } from 'react';

import { Modal } from '../Modal/Modal';
import { ProductNames } from '../../constants/ProductNames';
import { SHARE_PLATFORM_SEARCH_PARAM } from '../../utils/sharing/common';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useIsPageOwner } from '../../hooks/useIsPageOwner';
import { usePage } from '../Providers/PageProvider';
import { usePreview } from '../Providers/PreviewProvider';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { whenOwnerType } from '../../utils/ownerType';

const SharePage = dynamic(() =>
  import('../SharePage/SharePage').then((x) => x.SharePage),
);

type ShareButtonProps = {
  id: string;
  variant?: 'PRIMARY' | 'SECONDARY';
  className?: string;
};

export const ShareButton = ({
  id,
  variant = 'SECONDARY',
  className,
}: ShareButtonProps) => {
  const { t } = useTranslation();
  const search = useSearchParams();
  const [open, setOpen] = useState(
    Boolean(search.get(SHARE_PLATFORM_SEARCH_PARAM)),
  );
  const page = usePage();
  const isPageOwner = useIsPageOwner();
  const { isPreview } = usePreview();

  function getHeading(): string {
    if (isPageOwner) {
      return t('sharePage.heading.pageOwner');
    }

    if (page.product.name === ProductNames.InMemoryTeam) {
      return t('inMemoryTeam.share.heading', {
        personName: page.occasion?.names.at(0),
      });
    }

    return (
      whenOwnerType(page, {
        isIndividual: () =>
          t('sharePage.heading', { pageOwnerName: page.owner.name }),
        isPeerGroup: () =>
          t('sharePage.heading.team', { pageOwnerName: page.owner.name }),
        isOrganisation: () =>
          t('sharePage.heading.organisation', {
            organisation: page.charity.name,
          }),
      }) ?? ''
    );
  }

  function handleOnClick() {
    if (isPreview) return;

    setOpen(true);
  }

  return (
    <>
      <button
        id={id}
        className={classNames(
          className,
          'cp-btn',
          { 'cp-btn-primary': variant === 'PRIMARY' },
          { 'cp-btn-secondary': variant === 'SECONDARY' },
        )}
        onClick={handleOnClick}
      >
        {t('share')}
      </button>
      <Modal onClose={() => setOpen(false)} open={open}>
        <Modal.CloseButton />
        <SharePage
          heading={getHeading()}
          subheading={t('sharePage.subHeading')}
        />
      </Modal>
    </>
  );
};
